import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField
} from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { Loading } from '../../components/UI'
import { numberStyles, useStyles } from './styles'
import { PickingService } from '../../redux/services/pickingService'
import { PickingValidateSkuDetail } from '../../interfaces/Picking/Picking'
import { PickingDiffHeader } from './PickingDiff/PickingDiffHeader'
import {
  EnumTypeInventoryIncident,
  InventoryIncidentCreateDto,
  InventoryIncidentDto,
  InventoryIncidentFilterDto,
  InventoryIncidentInfoDto,
  InventoryIncidentInput,
  InventoryIncidentUpdateDto
} from '../../interfaces/PickingValidate/InventoryIncident'

import { ListElementText } from '../UI/ListElementText'
import { useUser } from '../../hooks/useUser'

interface PickingValidateExcessQtyProps {
  isOpen: boolean
  skuDetails: PickingValidateSkuDetail
  orderCancellationRequest: boolean
  handleCloseModal: (savedData: boolean, error?: string) => void
}

export const PickingValidateExcessQty: React.FC<
  PickingValidateExcessQtyProps
> = ({ isOpen, skuDetails, orderCancellationRequest, handleCloseModal }) => {
  const {
    userDataState: { userData }
  } = useUser()
  const serviceError = (message: string): void => {
    console.error(message)
    setIsLoading(false)
    handleCloseModal(true, `Error en operación: ${message}`)
  }

  const classes = useStyles()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pickingService] = useState<PickingService>(
    new PickingService(serviceError)
  )
  const [loadedIncident, setLoadedIncident] = useState<
    InventoryIncidentInfoDto | undefined
  >(undefined)
  const initialInput: InventoryIncidentInput = {
    quantity: 0
  }
  const [inputData, setInputData] =
    useState<InventoryIncidentInput>(initialInput)
  const [formValues, setValues] = useState(initialInput)
  const [formErrors, setErrors] = useState({} as any)

  useEffect(() => {
    if (!isOpen) return

    async function fetchData() {
      setIsLoading(true)
      const filters = new InventoryIncidentFilterDto()
      filters.pickingRouteId = skuDetails.pickingRouteId
      filters.stockKeepingUnitId = skuDetails.skuId
      filters.userId = userData?.userId!
      filters.typeInventoryIncidentId = EnumTypeInventoryIncident.Excess
      const data = await pickingService.getInventoryIncident(filters)
      setIsLoading(false)
      setLoadedIncident(data)
      setInputData({ ...inputData, quantity: data?.incidentQuantity || 0 })
      setValues({ ...formValues, quantity: data?.incidentQuantity || 0 })
    }
    fetchData()
  }, [isOpen])

  const setFieldError = (name: string, value: any, errors: any) => {
    if (name === 'quantity') {
      delete errors.quantity
      if (value === '') {
        errors.quantity = 'Campo requerido'
      }
    }
  }

  const validateInput = (name: string, value: any) => {
    const newErrors: any = { ...formErrors }
    setFieldError(name, value, newErrors)
    setErrors({ ...newErrors })
  }

  const handleQtyDec = () => {
    const qty = inputData.quantity - 1
    setInputData({ ...inputData, quantity: qty })
    setValues({ ...formValues, quantity: qty })
    validateInput('quantity', qty)
  }

  const handleQtyInc = () => {
    const qty = inputData.quantity + 1
    setInputData({ ...inputData, quantity: qty })
    setValues({ ...formValues, quantity: qty })
    validateInput('quantity', qty)
  }

  const handleInputValue = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    setValues({ ...formValues, [name]: value })
    validateInput(name, value)
    setInputData({ ...inputData, [name]: value })
  }

  const formIsValid = () => {
    const errors: any = {}
    Object.keys(formValues).forEach((key) => {
      setFieldError(key, formValues[key], errors)
    })
    setErrors({ ...errors })
    const isValid = Object.values(errors).every((x) => x === '')
    return isValid
  }

  const handleSubmit = (e: any) => {
    const isValid = formIsValid()
    if (isValid) {
      saveData()
    }
    e.preventDefault()
  }

  const getEditedDto = (): InventoryIncidentDto => {
    const dto = new InventoryIncidentDto()
    dto.stockKeepingUnitId = skuDetails.skuId
    dto.userId = userData?.userId!
    dto.pickingRouteId = skuDetails.pickingRouteId
    dto.typeInventoryIncidentId = EnumTypeInventoryIncident.Excess
    dto.incidentDate = new Date()
    dto.incidentQuantity = parseInt(inputData.quantity.toString())
    dto.flowId = 2
    return dto
  }

  const update = async (): Promise<InventoryIncidentInfoDto | undefined> => {
    const editedDto = getEditedDto()
    const dto = new InventoryIncidentUpdateDto()
    Object.assign(dto, editedDto)
    dto.inventoryIncidentId = loadedIncident?.inventoryIncidentId || -1
    const updated = await pickingService.updateInventoryIncident(dto)
    if (updated) return dto
    else return undefined
  }

  const create = async (): Promise<InventoryIncidentInfoDto | undefined> => {
    const editedDto = getEditedDto()
    const dto = new InventoryIncidentCreateDto()
    Object.assign(dto, editedDto)
    const createdDto = await pickingService.createInventoryIncident(dto)
    return createdDto
  }

  const saveData = async () => {
    setIsLoading(true)
    let resultDto: InventoryIncidentInfoDto | undefined
    if (loadedIncident) resultDto = await update()
    else resultDto = await create()
    if (resultDto) {
      skuDetails.excessQty = resultDto.incidentQuantity
      handleCloseModal(true)
    } else {
      handleCloseModal(true, 'Se produjo un error al guardar la información')
    }
    setIsLoading(false)
  }

  return (
    <Fragment>
      <PickingDiffHeader
        skuDetails={skuDetails}
        orderCancellationRequest={orderCancellationRequest}
        title='Informar Errores'
      ></PickingDiffHeader>

      <Card className={classes.root}>
        <div className={classes.details}>
          <CardContent className={classes.content}>
            <Grid container direction='row' spacing={2}>
              <Grid item xs={12} sm={3} lg={3}></Grid>
              <Grid item xs={12} sm={3} lg={3}>
                <ListElementText title='TOTAL RUTA' text={skuDetails.realQty} />
              </Grid>
              <Grid item xs={12} sm={2} lg={2}>
                <ListElementText
                  title='PICKEADAS'
                  text={skuDetails.pickedQty}
                />
              </Grid>
              <Grid item xs={12} sm={3} lg={3}></Grid>
            </Grid>
          </CardContent>
        </div>
      </Card>

      <Container maxWidth='xl'>
        {isLoading && <Loading />}
        {!isLoading && (
          <CardContent className={classes.content}>
            <Grid
              container
              spacing={2}
              direction='row'
              alignItems='center'
              justifyContent='space-around'
            >
              <Grid item xs={3}>
                <Button
                  size='small'
                  variant='contained'
                  color='primary'
                  onClick={handleQtyDec}
                >
                  -
                </Button>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id='quantity'
                  name='quantity'
                  label='Cantidad'
                  size='small'
                  autoComplete='off'
                  type='number'
                  onChange={handleInputValue}
                  value={inputData.quantity}
                  {...(formErrors.quantity && {
                    error: true,
                    helperText: formErrors.quantity
                  })}
                  style={numberStyles}
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleQtyInc}
                  size='small'
                >
                  +
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Container>

      <Grid container direction='row' justifyContent='center' spacing={2}>
        <Grid item xs={12} sm={3} lg={3}>
          <Button
            color='primary'
            size='small'
            variant='contained'
            fullWidth={true}
            onClick={handleSubmit}
            className={classes.actionButton}
            disabled={formErrors.quantity !== undefined || isLoading}
          >
            Guardar
          </Button>
        </Grid>
        <Grid item xs={12} sm={3} lg={3}>
          <Button
            color='inherit'
            size='small'
            variant='contained'
            fullWidth={true}
            onClick={() => handleCloseModal(false)}
            className={classes.actionButton}
            disabled={isLoading}
          >
            Cancelar
          </Button>
        </Grid>
      </Grid>
    </Fragment>
  )
}
